import { useCallback, useState, useContext } from 'react';
import { useFormContext, Controller } from 'react-hook-form';

import { PictureOutlined } from '@ant-design/icons';
import SaveOutlined from '@ant-design/icons/SaveOutlined';
import { notification, Tooltip } from 'antd';

import FormHelperText from '@mui/material/FormHelperText';
import Grid from '@mui/material/Grid';
import InputLabel from '@mui/material/InputLabel';
import MenuItem from '@mui/material/MenuItem';
import Select from '@mui/material/Select';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import ImageSelector, { IImageSelectorResult } from 'components/Shared/ImageSelector';
import { isEmpty } from 'helpers/lodash';
import { IEventForm } from 'interfaces/event';

import FormContext from '../../../Context';
import Actions from '../Actions';
import * as S from './styles';

export enum StatusForm {
  INACTIVE = 0,
  ACTIVE = 1
}

const FormOne = () => {
  const [imageSelectorOpen, setImageSelectorOpen] = useState(false);

  const {
    handleEdit,
    isCreate,
    loadingEdit,
    jobsLotPending,
    setCurrentStep,
    setStepsEnables,
    stepsEnables,
    eventCategories
  } = useContext(FormContext);

  const {
    register,
    formState: { errors },
    watch,
    setError,
    handleSubmit,
    setValue,
    clearErrors,
    control,
    getValues
  } = useFormContext<IEventForm>();

  const imageBase64Watch = watch('image_base64');
  const imagePathWatch = watch('image_path');
  const eventCategory = getValues('category_id');

  const handleNext = useCallback(() => {
    const enablesSteps = Array.from(stepsEnables);

    if (!enablesSteps.includes('2')) {
      enablesSteps.push('2');
      setStepsEnables(enablesSteps);
    }

    setCurrentStep('2');
  }, [stepsEnables, setCurrentStep, setStepsEnables]);

  const handleSelectImage = (image: IImageSelectorResult) => {
    if (!image) {
      setImageSelectorOpen(false);

      return;
    }

    setValue('image_base64', image.base64);
    setValue('image_path', undefined);
    setValue('image_name', image.filename);
    setValue('image_id', null);

    setImageSelectorOpen(false);
    clearErrors('image_base64');
  };

  const handleDeleteImage = () => {
    setValue('image_base64', null);
    setValue('image_name', null);
    setValue('image_path', undefined);
    setValue('image_id', null);
    setError('image_base64', { message: 'Selecione uma imagem' });
  };

  const handlePrimaryButtonAction = () => {
    if (control._formValues.active_data_capture && !control._formValues.data_capture_type) {
      notification.error({ message: 'Desative ou escolha um método para captação de dados!' });
      return;
    }

    if (!isEmpty(errors)) {
      notification.error({ message: 'Verifique todos os campos' });
      return;
    }

    handleSubmit(isCreate ? handleNext : handleEdit)();
  };

  return (
    <Grid container spacing={2}>
      <Grid item xs={12}>
        <S.Title>Dados básicos do seu evento</S.Title>
        <S.Subtitle>
          Por favor, dedique alguns minutos para preencher o formulário abaixo com os detalhes do seu evento.
        </S.Subtitle>
      </Grid>
      <Grid item xs={12}>
        <Grid container spacing={2} alignContent='center'>
          <Grid item xs={12} md={6}>
            {!imageBase64Watch && !imagePathWatch && (
              <Grid
                alignSelf='center'
                container
                item
                justifyContent='center'
                xs={12}
                sx={{
                  cursor: 'pointer',
                  background: '#FFFFFF',
                  border: 'solid 1px #BFBFBF',
                  height: '100%',
                  color: '#00000073'
                }}
                onClick={() => setImageSelectorOpen(true)}
              >
                <Grid container item justifyContent='center' alignItems='center' alignContent={'flex-end'} xs={12}>
                  <PictureOutlined style={{ fontSize: '64px' }} />
                </Grid>
                <Grid container item justifyContent='center' xs={12}>
                  <Typography>Adicionar uma capa</Typography>
                </Grid>
                {errors?.image_base64 && (
                  <Grid container item justifyContent='center' xs={12} sx={{ padding: '10px 0' }}>
                    <S.ErrorMessage>{errors?.image_base64?.message}</S.ErrorMessage>
                  </Grid>
                )}
              </Grid>
            )}
            {(imageBase64Watch || imagePathWatch) && (
              <Grid alignSelf='center' container item justifyContent='center' xs={12}>
                <div className='image-container'>
                  <S.Wrapper onClick={handleDeleteImage}>
                    <img src={imageBase64Watch || imagePathWatch} alt='Preview de foto do evento'></img>
                    <S.CustomizedDeleteIcon />
                  </S.Wrapper>
                  <FormHelperText>Imagem de capa</FormHelperText>
                </div>
              </Grid>
            )}

            {imageSelectorOpen && (
              <ImageSelector
                opened={imageSelectorOpen}
                height={200}
                width={200}
                onComplete={image => handleSelectImage(image)}
              />
            )}
          </Grid>
          <Grid item xs={12} md={6}>
            <Grid container spacing={2}>
              <Grid item xs={12}>
                <Controller
                  control={control}
                  name='title'
                  render={({ field: { onChange, value } }) => (
                    <>
                      <InputLabel>Nome do evento</InputLabel>
                      <TextField
                        fullWidth
                        variant='outlined'
                        InputLabelProps={{
                          shrink: true
                        }}
                        helperText={errors.title?.message}
                        error={!!errors.title}
                        onChange={event => {
                          onChange(event.target.value);
                        }}
                        value={value}
                        id='title-form-one'
                      />
                    </>
                  )}
                />
                <FormHelperText>Esse nome será exibido em todas as plataformas da Eduzz.</FormHelperText>
              </Grid>
              <Grid item xs={12}>
                <Controller
                  control={control}
                  name='helpsupport_email'
                  render={({ field: { onChange, value } }) => (
                    <>
                      <InputLabel>Email de suporte</InputLabel>
                      <TextField
                        fullWidth
                        className='help-support'
                        InputLabelProps={{
                          shrink: true
                        }}
                        helperText={errors.helpsupport_email?.message}
                        error={!!errors.helpsupport_email}
                        onChange={event => {
                          onChange(event.target.value.trim());
                        }}
                        value={value}
                        id='help_support-form-one'
                      />
                    </>
                  )}
                />
                <FormHelperText>As dúvidas de seus clientes serão direcionadas para este e-mail</FormHelperText>
              </Grid>
            </Grid>
          </Grid>
        </Grid>
      </Grid>
      <Grid item xs={12}>
        <InputLabel>Breve descrição do evento, contextualize seu cliente sobre o conteúdo do evento</InputLabel>
        <TextField
          fullWidth
          variant='outlined'
          helperText={errors.description?.message}
          error={!!errors.description}
          multiline
          InputLabelProps={{
            shrink: true
          }}
          rows={4}
          id='description-form-one'
          {...register('description')}
        />
      </Grid>
      <Grid item xs={12}>
        <Controller
          control={control}
          name='category_id'
          render={({ field: { onChange, value } }) => (
            <>
              <InputLabel>Categoria do evento</InputLabel>
              <Select
                fullWidth
                variant='outlined'
                defaultValue={eventCategory}
                value={value}
                required
                error={!!errors.category_id}
                id='select-category'
                onChange={event => {
                  onChange(event.target.value);
                }}
              >
                {eventCategories.map(category => {
                  return (
                    <MenuItem key={category.id} value={category.id}>
                      {category.title}
                    </MenuItem>
                  );
                })}
              </Select>
              <FormHelperText error>{errors.category_id?.message}</FormHelperText>
            </>
          )}
        />

        {watch('category_id') === 'CUSTOM_CATEGORY' && (
          <Controller
            control={control}
            name='custom_category_title'
            render={({ field: { onChange, value } }) => (
              <>
                <InputLabel>Categoria personalizada</InputLabel>
                <TextField
                  fullWidth
                  variant='outlined'
                  InputLabelProps={{
                    shrink: true
                  }}
                  helperText={errors.custom_category_title?.message}
                  value={value}
                  error={!!errors.custom_category_title}
                  onChange={onChange}
                  id='custom-category-title'
                />
              </>
            )}
          />
        )}
      </Grid>

      <Grid item xs={12}>
        <Actions>
          <Tooltip defaultOpen={!!jobsLotPending} open={!!jobsLotPending} title={jobsLotPending} color='#ffbc00'>
            <Actions.Button
              onClick={handlePrimaryButtonAction}
              size='large'
              id={isCreate ? 'step-forward-btn-form-1' : 'save-btn-form-1'}
              type='primary'
              icon={!isCreate && <SaveOutlined />}
              disabled={loadingEdit || !!jobsLotPending}
            >
              {isCreate ? 'Próximo' : 'Salvar'}
            </Actions.Button>
          </Tooltip>
        </Actions>
      </Grid>
    </Grid>
  );
};
export default FormOne;
